import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  LayoutDashboard, Type, Image, Link as LinkIcon, Plus, Trash2, Save, RefreshCw, Eye, EyeOff, X, BarChart2
} from "lucide-react";



const SERVER_URL = "https://allinone-clone-7d5d795e98ce.herokuapp.com";

const AdminAbout = () => {
  const [data, setData] = useState({
    subTitle: "",
    mainTitle: "",
    description: "",
    stats: [],
    button: { text: "", link: "" },
    images: { main: "", secondary: "", shape1: "", shape2: "" },
    isActive: true,
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/about-section`);
        if (res.data.success) {
          setData(res.data.data);
        }
      } catch (err) {
        showMessage("Failed to fetch data", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Handle Simple Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  // Handle Nested Object Change (Button, Images)
  const handleNestedChange = (category, field, value) => {
    setData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  // Handle Stats Change
  const handleStatChange = (index, field, value) => {
    const newStats = [...data.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setData({ ...data, stats: newStats });
  };

  const addStat = () => {
    const newStat = { id: Date.now().toString(), value: "", label: "" };
    setData({ ...data, stats: [...data.stats, newStat] });
  };

  const removeStat = (index) => {
    const newStats = data.stats.filter((_, i) => i !== index);
    setData({ ...data, stats: newStats });
  };

  // Submit Form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.put(`${SERVER_URL}/api/about-section`, data);
      showMessage("About section updated successfully!", "success");
    } catch (err) {
      showMessage("Failed to update", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset to default settings?")) return;
    try {
      const res = await axios.post(`${SERVER_URL}/api/about-section/reset`);
      if (res.data.success) {
        setData(res.data.data);
        showMessage("Reset successful", "success");
      }
    } catch (err) {
      showMessage("Failed to reset", "error");
    }
  };

  const toggleStatus = async () => {
    try {
      await axios.patch(`${SERVER_URL}/api/about-section/toggle`);
      const newStatus = !data.isActive;
      setData({ ...data, isActive: newStatus });
      showMessage(`Section ${newStatus ? "activated" : "deactivated"}`, "success");
    } catch (err) {
      showMessage("Failed to toggle status", "error");
    }
  };

  if (loading) return <div className="p-5 text-center">Loading Admin Panel...</div>;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h3 className="fw-bold">About Section Management</h3>
        <div className="d-flex gap-2">
            <button 
                className={`btn btn-sm ${data.isActive ? 'btn-success' : 'btn-outline-secondary'} d-flex align-items-center gap-2`}
                onClick={toggleStatus}
            >
                {data.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                {data.isActive ? "Active" : "Disabled"}
            </button>
          <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" onClick={handleReset} disabled={saving}>
            <RefreshCw size={18} /> Reset
          </button>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={handleSubmit} disabled={saving}>
            <Save size={18} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : message.type} mb-4`}>
          {message.text}
          <button type="button" className="btn-close float-end" onClick={() => setMessage(null)}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          
          {/* Images Section */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <Image size={20} /> Images
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-12 mb-3">
                            <label className="form-label small fw-bold">Main Image (Left)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.images?.main || ""}
                              onChange={(e) => handleNestedChange('images', 'main', e.target.value)}
                            />
                        </div>
                       
                    </div>
                </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <Type size={20} /> Content
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Sub Title</label>
                        <input
                          type="text"
                          name="subTitle"
                          className="form-control"
                          value={data.subTitle}
                          onChange={handleChange}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label small fw-bold">Main Title</label>
                        <input
                          type="text"
                          name="mainTitle"
                          className="form-control"
                          value={data.mainTitle}
                          onChange={handleChange}
                        />
                    </div>
                    <div className="mb-0">
                        <label className="form-label small fw-bold">Description</label>
                        <textarea
                          name="description"
                          className="form-control"
                          rows="4"
                          value={data.description}
                          onChange={handleChange}
                        />
                    </div>
                </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm">
                <div className="card-header bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <BarChart2 size={20} /> Statistics
                    </div>
                    <button type="button" className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={addStat}>
                        <Plus size={16} /> Add Stat
                    </button>
                </div>
                <div className="card-body">
                    {data.stats && data.stats.map((stat, index) => (
                        <div key={stat.id || index} className="row g-2 align-items-start mb-3 pb-3 border-bottom">
                            <div className="col-12 mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="badge bg-secondary">Stat #{index + 1}</span>
                                    <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => removeStat(index)}>
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <label className="small text-muted">Value</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Value (e.g. 200k+)"
                                  value={stat.value}
                                  onChange={(e) => handleStatChange(index, 'value', e.target.value)}
                                />
                            </div>
                            <div className="col-md-6 mb-2">
                                <label className="small text-muted">Label</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Label (e.g. Users)"
                                  value={stat.label}
                                  onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                                />
                            </div>
                        </div>
                    ))}
                    {(!data.stats || data.stats.length === 0) && <p className="text-center text-muted py-3">No statistics added.</p>}
                </div>
            </div>
          </div>

          {/* Button Section */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <LinkIcon size={20} /> CTA Button
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3 mb-md-0">
                            <label className="form-label small fw-bold">Button Text</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.button?.text || ""}
                              onChange={(e) => handleNestedChange('button', 'text', e.target.value)}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label small fw-bold">Button Link</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.button?.link || ""}
                              onChange={(e) => handleNestedChange('button', 'link', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default AdminAbout;